import { GetOrganizations } from 'core/API/workplaces';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { canViewOnboardingModule } from 'module/onboarding/utils/onboardingPermission';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useUserData } from 'store/useUserData';
import { OrganizationConfig } from 'types';
import { SecurityRolePermission } from 'tests/services/permissionService';
import { AuthForm } from '../components/AuthForm/AuthForm';
import { AuthLayout } from '../components/AuthLayout/AuthLayout';
import ChooseWorkplace from '../components/ChooseWorkplace/ChooseWorkplace';
import { useAuth } from '../context/AuthContext';

interface FormValues {
	email: string;
	password: string;
	organizationId: string;
}

export default function LoginPage(): JSX.Element {
	const auth = useAuth();
	const { push, location } = useHistory();
	const [view, setView] = useState('auth');
	const [isLoading, setIsLoading] = useState(false);

	const { data: orgData } = useQuery<OrganizationConfig>('org', GetOrganizations, {
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		enabled: auth.isLoggedin(),
	});
	const products: any[] = (orgData as any)?.[0]?.products || [];

	const { hasModuleViewPermission, hasViewPermission } = usePermissionCheck();
	const { currentUserRole } = useUserData();
	const onboardingRolePermissions = (currentUserRole?.securityRolePermission ??
		[]) as SecurityRolePermission[];

	const isProductAvailable = (label: string) =>
		products.length === 0 || products.some((p) => p.label === label);

	const getFirstAccessibleRoute = (): string => {
		const checks: Array<{ route: string; productLabel?: string; permitted: boolean }> = [
			{
				route: ROUTES.DASHBOARD,
				permitted: hasModuleViewPermission('Dashboard'),
			},
			{
				route: ROUTES.SCORECARD,
				productLabel: 'Scorecard',
				permitted: hasModuleViewPermission('Scorecard'),
			},
			{
				route: ROUTES.GUESTCONNECT,
				productLabel: 'Guest Connect',
				permitted: hasModuleViewPermission('Guest Connect'),
			},
			{
				route: ROUTES.CHECKLIST,
				productLabel: 'Checklist',
				permitted:
					hasModuleViewPermission('Checklist - My') ||
					hasModuleViewPermission('Checklist - All'),
			},
			{
				route: ROUTES.TO_DO,
				productLabel: 'TODO',
				permitted:
					hasModuleViewPermission('To-Do - My') ||
					hasModuleViewPermission('To-Do - All'),
			},
			{
				route: ROUTES.LOGBOOK,
				productLabel: 'Logbook',
				permitted: hasModuleViewPermission('Logbook'),
			},
			{
				route: ROUTES.EVENTS,
				productLabel: 'Events',
				permitted: hasModuleViewPermission('Events'),
			},
			{
				route: ROUTES.HYPERSCHEDULER,
				productLabel: 'Scheduler',
				permitted:
					hasViewPermission('Schedule - My') ||
					hasViewPermission('Schedule - All'),
			},
			{
				route: ROUTES.BROADCAST,
				productLabel: 'Broadcast',
				permitted:
					hasModuleViewPermission('Broadcast - My') ||
					hasModuleViewPermission('Broadcast - All') ||
					hasModuleViewPermission('Broadcast - Organization'),
			},
			{
				route: ROUTES.GAMEMASTER,
				productLabel: 'Game Master',
				permitted: hasModuleViewPermission('GameMaster'),
			},
			{
				route: ROUTES.ONBOARDING,
				productLabel: 'Onboarding',
				permitted: canViewOnboardingModule(onboardingRolePermissions),
			},
		];

		const match = checks.find(
			({ productLabel, permitted }) =>
				(productLabel ? isProductAvailable(productLabel) : true) && permitted
		);

		return match?.route ?? ROUTES.DASHBOARD;
	};

	useEffect(() => {
		if (auth.isLoggedin()) {
			if (auth.isWorkplaceSelected()) {
				// if state exists that means its redirected from the app
				if (location.state) {
					setView('chooseworkplace');
				} else {
					push(getFirstAccessibleRoute());
				}
			} else {
				setView('chooseworkplace');
			}
		} else {
			return;
		}
	}, []);

	const submitClick = async (data: FormValues) => {
		setIsLoading(true);
		try {
			const tenant = data.organizationId;
			delete data.organizationId;
			if (await auth.login({ ...data, tenant })) {
				setView('chooseworkplace');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const chooseWorkplace = () => {
		push(getFirstAccessibleRoute());
	};

	return (
		<AuthLayout>
			{view === 'auth' ? (
				<AuthForm submitClick={submitClick} isLoading={isLoading} />
			) : (
				<ChooseWorkplace chooseWorkplace={chooseWorkplace} />
			)}
		</AuthLayout>
	);
}
